<template>
  <div class="application-component technical-flow-component">
    <div class="application-component-title">方案拆解</div>
    <div
      class="application-component-content"
      v-loading="!readOnly && generating"
      element-loading-text="正在思考中..."
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="5" animated />
      </div>
      <template v-else>
        <div v-if="hasDiagram" class="flow-toolbar">
          <div class="flow-stats">
            <span class="flow-stat">{{ diagramData.nodes.length }} 个节点</span>
            <span class="flow-stat">{{ diagramData.edges.length }} 条关系</span>
            <span class="flow-stat">{{ diagramData.direction === 'TD' ? '纵向' : '横向' }}展示</span>
          </div>
          <el-button type="primary" size="small" :loading="generating" @click="handleGenerate()">
            重新生成
          </el-button>
        </div>

        <div v-if="hasDiagram" class="flow-layout">
          <section class="flow-stage">
            <div class="flow-summary-card">
              <div class="flow-summary-title">{{ diagramData.title || '方案拆解' }}</div>
              <div class="flow-summary-text">
                {{ diagramData.summary || '根据技术交底书自动梳理技术实现路径和模块关系。' }}
              </div>
            </div>

            <div class="flow-canvas-card">
              <div v-if="renderError" class="flow-render-error">{{ renderError }}</div>
              <div ref="diagramRef" class="flow-diagram" />
            </div>
          </section>

          <section class="flow-node-panel">
            <div class="flow-node-panel-title">节点定位</div>
            <button
              v-for="node in diagramData.nodes"
              :key="node.id"
              type="button"
              class="flow-node-card"
              :class="{ active: activeNodeId === node.id }"
              @click="handleSelectNode(node)"
            >
              <div class="flow-node-head">
                <span class="flow-node-name">{{ node.label }}</span>
                <span class="flow-node-type">{{ formatNodeType(node.type) }}</span>
              </div>
              <div v-if="node.description" class="flow-node-desc">{{ node.description }}</div>
              <div class="flow-node-source" :class="{ empty: !node.sourceRanges.length }">
                {{ buildSourceSummary(node) }}
              </div>
            </button>
          </section>
        </div>

        <div v-else class="flow-empty-wrap">
          <el-empty>
            <template #description>
              <div class="flow-empty-copy">
                <div class="flow-empty-title">首次进入会自动生成方案拆解</div>
                <div class="flow-empty-desc">如果自动生成失败，可以在这里重新触发。</div>
              </div>
            </template>
            <el-button type="primary" size="small" :loading="generating" @click="handleGenerate()">
              重新生成
            </el-button>
          </el-empty>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useWritingStore } from '@/store/modules/writing'
import { fetchGenerateTechnicalFlow } from '@/api/writing'
import { jsonParse } from '@/utils'

const COMPONENT_TYPE_CODE = 'comp_tfl'
const TYPE_LABEL_MAP = {
  start: '起始',
  process: '步骤',
  decision: '判断',
  module: '模块',
  output: '输出',
}

const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => null,
  },
  projectId: {
    type: String,
    default: '',
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null,
  },
})

const diagramRef = ref(null)
const diagramData = ref(createEmptyResult())
const generating = ref(false)
const renderError = ref('')
const activeNodeId = ref('')
const localExtOne = ref('')
const hasAutoTriggered = ref(false)

let mermaidApi = null
let renderSequence = 0

const hasDiagram = computed(() => diagramData.value.nodes.length > 0)

function createEmptyResult() {
  return {
    title: '',
    summary: '',
    direction: 'LR',
    nodes: [],
    edges: [],
  }
}

const normalizeDirection = (value) => {
  return String(value || '').toUpperCase() === 'TD' ? 'TD' : 'LR'
}

const normalizeNodeType = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (['start', 'process', 'decision', 'module', 'output'].includes(normalized)) {
    return normalized
  }
  return 'process'
}

const normalizeNodeId = (value, index) => {
  const normalized = String(value || `node_${index + 1}`)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')

  const candidate = normalized || `node_${index + 1}`
  return /^[a-z]/.test(candidate) ? candidate : `node_${index + 1}`
}

const ensureUniqueNodeId = (candidate, usedIds) => {
  if (!usedIds.has(candidate)) {
    usedIds.add(candidate)
    return candidate
  }

  let suffix = 2
  while (usedIds.has(`${candidate}_${suffix}`)) {
    suffix += 1
  }

  const nextValue = `${candidate}_${suffix}`
  usedIds.add(nextValue)
  return nextValue
}

const normalizeSourceRanges = (sourceRanges = []) => {
  if (!Array.isArray(sourceRanges)) {
    return []
  }

  return sourceRanges
    .map((item) => {
      const paragraphIndex = Number(item?.paragraphIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)

      return {
        paragraphIndex: Number.isFinite(paragraphIndex) ? paragraphIndex : null,
        anchorId: item?.anchorId || (Number.isFinite(paragraphIndex) ? `note-paragraph-${paragraphIndex}` : ''),
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
        quote: item?.quote || '',
      }
    })
    .filter(item =>
      Number.isInteger(item.paragraphIndex) &&
      Number.isInteger(item.startOffset) &&
      Number.isInteger(item.endOffset) &&
      item.endOffset > item.startOffset
    )
}

const normalizeParagraphIndices = (sourceRanges, paragraphIndices = []) => {
  return Array.from(new Set([
    ...(Array.isArray(paragraphIndices) ? paragraphIndices : []),
    ...sourceRanges.map(item => item.paragraphIndex),
  ].map(value => Number(value)).filter(value => Number.isInteger(value))))
}

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || Array.isArray(parsed)) {
    return createEmptyResult()
  }

  const usedIds = new Set()
  const nodeIdMap = new Map()
  const sourceNodes = Array.isArray(parsed.nodes) ? parsed.nodes : []
  const nodes = sourceNodes
    .map((item, index) => {
      const label = String(item?.label || item?.name || item?.title || '').trim()
      if (!label) {
        return null
      }

      const sourceRanges = normalizeSourceRanges(item?.sourceRanges)
      const nextId = ensureUniqueNodeId(normalizeNodeId(item?.id, index), usedIds)
      if (item?.id) {
        nodeIdMap.set(String(item.id), nextId)
      }
      nodeIdMap.set(nextId, nextId)

      return {
        id: nextId,
        label,
        type: normalizeNodeType(item?.type),
        description: String(item?.description || '').trim(),
        sourceRanges,
        paragraphIndices: normalizeParagraphIndices(sourceRanges, item?.paragraphIndices),
      }
    })
    .filter(Boolean)

  const nodeIds = new Set(nodes.map(item => item.id))
  const sourceEdges = Array.isArray(parsed.edges) ? parsed.edges : []
  const edges = sourceEdges
    .map((item, index) => {
      const from = nodeIdMap.get(String(item?.from || '')) || String(item?.from || '')
      const to = nodeIdMap.get(String(item?.to || '')) || String(item?.to || '')
      if (!nodeIds.has(from) || !nodeIds.has(to) || from === to) {
        return null
      }

      return {
        id: item?.id || `edge_${index + 1}`,
        from,
        to,
        label: String(item?.label || '').trim(),
      }
    })
    .filter(Boolean)

  const normalizedEdges = edges.length || nodes.length < 2
    ? edges
    : nodes.slice(0, -1).map((item, index) => ({
      id: `edge_${index + 1}`,
      from: item.id,
      to: nodes[index + 1].id,
      label: '',
    }))

  return {
    title: String(parsed.title || '').trim(),
    summary: String(parsed.summary || parsed.description || '').trim(),
    direction: normalizeDirection(parsed.direction),
    nodes,
    edges: normalizedEdges,
  }
}

const hasLinkedSource = (result) => {
  const nodes = Array.isArray(result?.nodes) ? result.nodes : []
  return nodes.some(node =>
    Array.isArray(node?.sourceRanges) && node.sourceRanges.length ||
    Array.isArray(node?.paragraphIndices) && node.paragraphIndices.length
  )
}

const isLegacyInheritedTechnicalFlow = (extOne) => {
  const metadata = typeof extOne === 'string' ? jsonParse(extOne, null) : extOne
  return metadata?.rawAnswer === '[SYSTEM] inherited_from_step1_technical_solution'
}

const shouldAutoRepairLegacyDiagram = () => {
  const persistedResult = normalizeResult(props.data?.docResult)
  if (!persistedResult.nodes.length) {
    return false
  }
  if (hasLinkedSource(persistedResult)) {
    return false
  }
  return isLegacyInheritedTechnicalFlow(props.data?.extOne)
}

const syncStepState = (docResult, extOne = localExtOne.value) => {
  const nextStep2Info = {
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [COMPONENT_TYPE_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE] || {}),
        ...(props.data || {}),
        docResult,
        extOne,
      },
    },
  }

  writingStore.setStep2Info(nextStep2Info)
}

const formatNodeType = (type) => TYPE_LABEL_MAP[type] || TYPE_LABEL_MAP.process

const sanitizeMermaidLabel = (value) => {
  return String(value || '')
    .replace(/\r?\n+/g, ' ')
    .replace(/"/g, '\\"')
    .replace(/[\[\]\{\}\|]/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

const buildNodeExpression = (node) => {
  const label = sanitizeMermaidLabel(node.label)
  switch (node.type) {
    case 'start':
      return `${node.id}(["${label}"])`
    case 'decision':
      return `${node.id}{"${label}"}`
    case 'module':
      return `${node.id}[["${label}"]]`
    case 'output':
      return `${node.id}(["${label}"])`
    default:
      return `${node.id}["${label}"]`
  }
}

const buildEdgeExpression = (edge) => {
  const label = sanitizeMermaidLabel(edge.label)
  if (!label) {
    return `${edge.from} --> ${edge.to}`
  }
  return `${edge.from} -->|${label}| ${edge.to}`
}

const buildClassList = (node) => {
  const result = [`tf_${node.type || 'process'}`]
  if (activeNodeId.value === node.id) {
    result.push('tf_active')
  }
  return result.join(',')
}

const buildMermaidSource = () => {
  const lines = [
    `flowchart ${diagramData.value.direction}`,
    'classDef tf_start fill:#ecfdf5,stroke:#0f766e,stroke-width:1.6px,color:#134e4a;',
    'classDef tf_process fill:#ffffff,stroke:#cbd5e1,stroke-width:1.5px,color:#0f172a;',
    'classDef tf_decision fill:#fff7ed,stroke:#ea580c,stroke-width:1.6px,color:#9a3412;',
    'classDef tf_module fill:#eff6ff,stroke:#2563eb,stroke-width:1.6px,color:#1d4ed8;',
    'classDef tf_output fill:#f8fafc,stroke:#475569,stroke-width:1.6px,color:#0f172a;',
    'classDef tf_active fill:#fef3c7,stroke:#d97706,stroke-width:2.4px,color:#92400e;',
  ]

  diagramData.value.nodes.forEach((node) => {
    lines.push(buildNodeExpression(node))
  })
  diagramData.value.edges.forEach((edge) => {
    lines.push(buildEdgeExpression(edge))
  })
  diagramData.value.nodes.forEach((node) => {
    lines.push(`class ${node.id} ${buildClassList(node)}`)
  })

  return lines.join('\n')
}

const loadMermaid = async () => {
  if (mermaidApi) {
    return mermaidApi
  }

  const module = await import('mermaid')
  mermaidApi = module.default || module
  mermaidApi.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: 'base',
    flowchart: {
      htmlLabels: false,
      curve: 'basis',
    },
    themeVariables: {
      fontFamily: 'inherit',
      lineColor: '#94a3b8',
      primaryColor: '#ffffff',
      primaryTextColor: '#0f172a',
      primaryBorderColor: '#cbd5e1',
      secondaryColor: '#ecfdf5',
      tertiaryColor: '#eff6ff',
    },
  })
  return mermaidApi
}

const bindNodeClickEvents = () => {
  const nodeMap = new Map(diagramData.value.nodes.map(item => [item.id, item]))
  const nodeElements = diagramRef.value?.querySelectorAll('.node[data-id]') || []

  nodeElements.forEach((element) => {
    const nodeId = element.getAttribute('data-id')
    if (!nodeId || !nodeMap.has(nodeId)) {
      return
    }

    element.classList.add('is-clickable')
    element.addEventListener('click', () => {
      handleSelectNode(nodeMap.get(nodeId))
    })
  })
}

const renderDiagram = async () => {
  if (!diagramRef.value) {
    return
  }

  if (!diagramData.value.nodes.length) {
    diagramRef.value.innerHTML = ''
    renderError.value = ''
    return
  }

  const currentSequence = renderSequence + 1
  renderSequence = currentSequence

  try {
    const mermaid = await loadMermaid()
    const renderId = `technical-flow-${Date.now()}-${currentSequence}`
    const { svg } = await mermaid.render(renderId, buildMermaidSource())
    if (currentSequence !== renderSequence || !diagramRef.value) {
      return
    }

    diagramRef.value.innerHTML = svg
    bindNodeClickEvents()
    renderError.value = ''
  } catch (error) {
    console.error('technical flow render failed', error)
    if (diagramRef.value) {
      diagramRef.value.innerHTML = ''
    }
    renderError.value = '流程图渲染失败，请重新生成。'
  }
}

const buildSourceSummary = (node) => {
  const paragraphIndices = normalizeParagraphIndices(node?.sourceRanges || [], node?.paragraphIndices || [])
  if (!paragraphIndices.length) {
    return '未关联原文片段'
  }
  return `关联段落：${paragraphIndices.join('、')}`
}

const handleSelectNode = async (node) => {
  if (!node) {
    return
  }

  activeNodeId.value = node.id
  await nextTick()

  if (!node.sourceRanges.length) {
    ElMessage.warning('当前节点暂无可定位的原文片段')
    return
  }

  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: node.id,
      sourceRanges: node.sourceRanges,
    })
  }
}

const handleGenerate = async ({ auto = false } = {}) => {
  if (!props.projectId || generating.value) {
    return
  }

  generating.value = true
  try {
    const result = normalizeResult(await fetchGenerateTechnicalFlow(props.projectId))
    if (!result.nodes.length) {
      throw new Error('technical flow result is empty')
    }

    diagramData.value = result
    activeNodeId.value = result.nodes[0]?.id || ''
    syncStepState(JSON.stringify(result), localExtOne.value || props.data?.extOne || '')
    await nextTick()
    await renderDiagram()

    if (!auto) {
      ElMessage.success('方案拆解生成完成')
    }
  } catch (error) {
    console.error('technical flow generate failed', error)
    ElMessage.error(auto ? '方案拆解自动生成失败' : '方案拆解生成失败')
  } finally {
    generating.value = false
  }
}

const tryAutoGenerate = async () => {
  const projectId = String(props.projectId || '')
  if (!projectId || props.readOnly || generating.value || hasAutoTriggered.value) {
    return
  }

  const shouldRepairLegacyDiagram = shouldAutoRepairLegacyDiagram()
  if (hasDiagram.value && !shouldRepairLegacyDiagram) {
    return
  }

  const persistedResult = normalizeResult(props.data?.docResult)
  if (persistedResult.nodes.length && !shouldRepairLegacyDiagram) {
    return
  }

  hasAutoTriggered.value = true
  await handleGenerate({ auto: true })
}

watch(
  () => props.data?.docResult,
  async (newValue) => {
    diagramData.value = normalizeResult(newValue)
    if (activeNodeId.value && !diagramData.value.nodes.some(item => item.id === activeNodeId.value)) {
      activeNodeId.value = diagramData.value.nodes[0]?.id || ''
    }
    await nextTick()
    await renderDiagram()
  },
  { immediate: true }
)

watch(
  () => props.projectId,
  () => {
    hasAutoTriggered.value = false
  },
  { immediate: true }
)

watch(
  () => props.data?.extOne,
  (newValue) => {
    localExtOne.value = newValue || ''
  },
  { immediate: true }
)

watch(
  () => [props.projectId, props.readOnly, props.data?.docResult || '', diagramData.value.nodes.length],
  async () => {
    await tryAutoGenerate()
  },
  { immediate: true }
)

watch(
  () => activeNodeId.value,
  async () => {
    if (!diagramData.value.nodes.length) {
      return
    }
    await nextTick()
    await renderDiagram()
  }
)

onUnmounted(() => {
  renderSequence += 1
  if (diagramRef.value) {
    diagramRef.value.innerHTML = ''
  }
})
</script>

<style scoped lang="scss">
@import './common.scss';

.flow-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.flow-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.flow-stat {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  color: #0f766e;
  font-size: 12px;
  font-weight: 600;
  background: #ecfdf5;
  border-radius: 999px;
}

.flow-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(260px, 0.9fr);
  gap: 16px;
  min-height: 0;
}

.flow-stage {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.flow-summary-card,
.flow-canvas-card,
.flow-node-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.flow-summary-card {
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #eefbf8 100%);
}

.flow-summary-title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
}

.flow-summary-text {
  margin-top: 6px;
  color: #475569;
  line-height: 1.7;
}

.flow-canvas-card {
  position: relative;
  min-height: 360px;
  padding: 8px;
}

.flow-diagram {
  height: 100%;
  min-height: 344px;
  overflow: auto;
  border-radius: 12px;
  background:
    radial-gradient(circle at top left, rgba(15, 118, 110, 0.08), transparent 42%),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.flow-render-error {
  margin: 8px;
  padding: 10px 12px;
  color: #b91c1c;
  font-size: 12px;
  background: #fef2f2;
  border-radius: 10px;
}

.flow-node-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  overflow: auto;
}

.flow-node-panel-title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
}

.flow-node-card {
  width: 100%;
  padding: 12px;
  text-align: left;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #99f6e4;
    transform: translateY(-1px);
  }

  &.active {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.08);
  }
}

.flow-node-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.flow-node-name {
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
}

.flow-node-type {
  color: #0f766e;
  font-size: 12px;
  font-weight: 600;
}

.flow-node-desc {
  margin-top: 8px;
  color: #475569;
  font-size: 12px;
  line-height: 1.6;
}

.flow-node-source {
  margin-top: 8px;
  color: #0f766e;
  font-size: 12px;

  &.empty {
    color: #9ca3af;
  }
}

.flow-empty-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

.flow-empty-copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flow-empty-title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
}

.flow-empty-desc {
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

:deep(.flow-diagram svg) {
  min-width: 100%;
}

:deep(.flow-diagram .node.is-clickable) {
  cursor: pointer;
}

:deep(.flow-diagram .node.is-clickable:hover rect),
:deep(.flow-diagram .node.is-clickable:hover polygon),
:deep(.flow-diagram .node.is-clickable:hover path),
:deep(.flow-diagram .node.is-clickable:hover circle) {
  filter: brightness(0.98);
}

@media (max-width: 1200px) {
  .flow-layout {
    grid-template-columns: 1fr;
  }

  .flow-node-panel {
    max-height: 320px;
  }
}
</style>
